/**
 * shared/calc を IndexedDB のデータへ適用するフック。
 *
 * 計算そのものは shared/calc（純関数）にあり、ここは「今日」を渡して
 * 結果をメモ化するだけ。通知 Worker（Phase 5）も同じ関数を呼ぶので、
 * 表示と通知の予測がズレない（SPEC §2.4-2）。
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';
import {
  buildIndex,
  forecast,
  monthlySummaries,
  priceHistory,
  shoppingList,
  type CalcIndex,
  type Dataset,
  type Forecast,
  type MonthSummary,
  type PriceHistory,
  type ShoppingCandidate,
} from '../../shared/calc';
import { lastMonths } from '../../shared/date';
import type { ItemRow, LotRow, PurchaseRow } from '../../shared/types';
import { db } from '../lib/db';
import { today } from '../lib/date';

const EMPTY: Dataset = { items: [], purchases: [], lots: [] };

export interface DatasetState {
  loaded: boolean;
  data: Dataset;
  /** その描画時点での業務日付（YYYY-MM-DD） */
  today: string;
}

export function useDataset(): DatasetState {
  const raw = useLiveQuery(async () => {
    const [items, purchases, lots] = await Promise.all([
      db.items.toArray(),
      db.purchases.toArray(),
      db.lots.toArray(),
    ]);
    return { items, purchases, lots } satisfies Dataset;
  }, []);

  // today() は文字列を返すので、日付が変わらない限り memo は維持される
  const t = today();
  return useMemo(
    () => ({ loaded: raw !== undefined, data: raw ?? EMPTY, today: t }),
    [raw, t],
  );
}

export interface CalcState extends DatasetState {
  index: CalcIndex;
}

export function useCalc(): CalcState {
  const state = useDataset();
  const index = useMemo(() => buildIndex(state.data), [state.data]);
  return { ...state, index };
}

/* ------------------------------------------------------------------ */

export interface MonthlyState {
  loaded: boolean;
  months: string[];
  summaries: MonthSummary[];
  /** 末尾（＝当月）の集計。データ未読込でも 0 埋めで返る */
  current: MonthSummary;
}

const emptySummary = (month: string): MonthSummary => ({
  month,
  accrual: 0,
  accrualConfirmed: 0,
  accrualEstimated: 0,
  cash: 0,
  byCategory: {},
  byItem: [],
});

/** 直近 monthCount ヶ月の月次集計（SPEC §7.3） */
export function useMonthly(monthCount = 12): MonthlyState {
  const { loaded, data, today: t } = useDataset();
  const months = useMemo(() => lastMonths(t.slice(0, 7), monthCount), [t, monthCount]);
  const summaries = useMemo(() => monthlySummaries(data, t, months), [data, t, months]);
  return {
    loaded,
    months,
    summaries,
    current: summaries[summaries.length - 1] ?? emptySummary(t.slice(0, 7)),
  };
}

/* ------------------------------------------------------------------ */

export interface ItemDetailState {
  loaded: boolean;
  today: string;
  item: ItemRow | null;
  purchases: PurchaseRow[];
  /** 開封が新しい順 */
  lots: LotRow[];
  forecast: Forecast | null;
  price: PriceHistory | null;
  /** 平均使用日数（確定ロットの実績。無ければ default_duration_days） */
  averageDurationDays: number;
  averageBasis: 'history' | 'default';
  /** 直近 12 ヶ月のこの品目の発生主義費用の平均 */
  monthlyAverage: number;
}

export function useItemDetail(itemId: string): ItemDetailState {
  const { loaded, data, index, today: t } = useCalc();

  return useMemo(() => {
    const item = index.itemById.get(itemId) ?? null;
    if (!item) {
      return {
        loaded,
        today: t,
        item: null,
        purchases: [],
        lots: [],
        forecast: null,
        price: null,
        averageDurationDays: 0,
        averageBasis: 'default' as const,
        monthlyAverage: 0,
      };
    }

    const f = forecast(index, itemId, t);
    const months = lastMonths(t.slice(0, 7), 12);
    const summaries = monthlySummaries(data, t, months);
    const total = summaries.reduce(
      (a, s) => a + (s.byItem.find((x) => x.itemId === itemId)?.total ?? 0),
      0,
    );

    return {
      loaded,
      today: t,
      item,
      purchases: (index.purchasesByItem.get(itemId) ?? []).slice().reverse(),
      lots: (index.lotsByItem.get(itemId) ?? []).slice().reverse(),
      forecast: f,
      price: priceHistory(index, itemId),
      averageDurationDays: f.averageDurationDays,
      averageBasis: f.basis,
      monthlyAverage: total / months.length,
    };
  }, [loaded, data, index, itemId, t]);
}

/* ------------------------------------------------------------------ */

/** 全品目の在庫切れ予測（SPEC §6.3）。品目 ID → Forecast */
export function useForecasts(): Map<string, Forecast> {
  const { index, today: t } = useCalc();
  return useMemo(
    () => new Map(index.items.map((i) => [i.id, forecast(index, i.id, t)])),
    [index, t],
  );
}

/** 買い物リストの自動掲載分（SPEC §6.4） */
export function useShoppingCandidates(thresholdDays: number): ShoppingCandidate[] {
  const { data, today: t } = useDataset();
  return useMemo(() => shoppingList(data, t, thresholdDays), [data, t, thresholdDays]);
}
